/**
 * AnalyticsDateRangeDrawerField — the date range's FALLBACK home inside the Filters
 * drawer (Task 3, VERA-260).
 *
 * ## Why this exists now, when it did not before
 *
 * The date range used to be toolbar-only, "on purpose" — see `AnalyticsFiltersHeader`'s own
 * docblock on `dateRangeControl` for the original reasoning (a page-scoping control belongs
 * where it is always visible, not behind a panel). That reasoning still holds WHILE the
 * toolbar has room for it. What changed is that the toolbar control can now DROP off a
 * narrow viewport (`TOOLBAR_DROP_SX`, VERA-260 Task 2) rather than wrapping the row, and a
 * range that is toolbar-only AND sometimes-hidden would have no way to be set at all on a
 * narrow screen. So: toolbar when it fits, this field as the fallback home, one shared
 * redux state (`state.analytics.filters.dateRange`) either way.
 *
 * ## Why this is a SEPARATE component rather than reusing `dateRangeControl` directly
 *
 * `AnalyticsFiltersHeader`'s own `dateRangeControl` is a single JSX element built from a
 * `pickerOpen` boolean that lives in that component's state — ONE flag shared by whichever
 * `DateTimeRangePicker` reads it. Both the toolbar control and this drawer field can be
 * mounted AT THE SAME TIME (wide viewport, drawer open), so if they shared that flag,
 * opening either picker would open both — two live poppers fighting over one `open` state
 * and one calendar. This component owns its OWN `open` state, so two independent instances
 * (toolbar `dateRangeControl`, drawer `AnalyticsDateRangeDrawerField`) never collide.
 *
 * ## Why this is NOT a `columns` entry
 *
 * See `AnalyticsFiltersHeader`'s `columns` — the `location` entry's own comment explains why
 * the date range is routed through `ActionLine`'s `extraFilters` slot instead: a `columns`
 * entry becomes an `internalFilters` member in `Filters.tsx`, which drives the Filter
 * button's applied-count badge — built for OPTIONAL facets that can be zero. The date range
 * is never zero, so it would pin that badge at "2" permanently. `extraFilters` is the same
 * escape hatch `ProductsPage`'s "Show archived" toggle already uses for the identical
 * reason (a real, editable control that is not one of the badge-counted facets) — this field
 * writes STRAIGHT to redux on change rather than through the drawer's staged/Apply
 * machinery `extraFilters` sites more commonly use, because there is no "unapplied draft"
 * state for a range that is always live (matching `dateRangeControl`'s own immediate-commit
 * behaviour).
 *
 * ## What is deliberately SIMPLER here than the toolbar's own field
 *
 * No preset-name-in-the-field adornment, no measured content-sizing, no
 * whole-day-vs-timed format switch — this is a plain `fullWidth` field matching every other
 * drawer field's chrome (`dateRangeFieldSx`, the same one `DateRangePickerFilter` itself
 * uses), because the drawer's own fields are already full-width and there is nothing here
 * for a compact trigger box to compress. It still supports TIME (a `DateTimeRangePicker`,
 * not `DateRangePickerFilter`'s day-only `DateRangePicker`), so a range set from either
 * surface round-trips through the other without losing precision — only the toolbar's fancy
 * presentation is not reproduced.
 */
import React, { useState } from 'react'

import dayjs, { Dayjs } from 'dayjs'

import { DateTimeRangePicker, SingleInputDateTimeRangeField, type DateRange } from '@mui/x-date-pickers-pro'

import { FilterField } from 'components/input/filters/FilterField'
import {
  DATE_TIME_RANGE_FIELD_FORMAT,
  DateRangeCalendarHeader,
  DateRangePickerDay,
  dateRangeDaySx,
  dateRangeDesktopPaperSx,
  dateRangeFieldSx,
  dateRangeShortcutsSx,
} from 'components/input/filters/dateRangeChrome'

interface AnalyticsDateRangeDrawerFieldProps {
  value: { from: Date; to: Date }
  onChange: (range: { from: Date; to: Date }) => void
  /** The SAME company-aware preset list `AnalyticsFiltersHeader` builds for the toolbar
   *  picker (includes "This billing period" once the boundaries are known) — passed in
   *  rather than recomputed here, so there is one `shortcutItems` calculation, not two. */
  shortcutItems: any[]
}

export const AnalyticsDateRangeDrawerField = ({
  value,
  onChange,
  shortcutItems,
}: AnalyticsDateRangeDrawerFieldProps) => {
  const [open, setOpen] = useState(false)

  const dateRangeValue: DateRange<Dayjs> = [dayjs(value.from), dayjs(value.to)]

  const handleChange = (newValue: DateRange<Dayjs>) => {
    if (newValue[0] && newValue[1]) {
      onChange({ from: newValue[0].toDate(), to: newValue[1].toDate() })
    }
  }

  return (
    <FilterField label="Date range">
      <DateTimeRangePicker
        value={dateRangeValue}
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // Same reasoning as the toolbar field: caps the future at end-of-today rather than
        // `disableFuture`'s day-only comparison, which misfires on a DATE-TIME picker against
        // a day-boundaried value (see `AnalyticsFiltersHeader`'s identical prop for the full
        // explanation).
        maxDateTime={dayjs().endOf('day')}
        format={DATE_TIME_RANGE_FIELD_FORMAT}
        slots={{
          field: SingleInputDateTimeRangeField,
          day: DateRangePickerDay,
          calendarHeader: DateRangeCalendarHeader,
        }}
        slotProps={{
          desktopPaper: { sx: dateRangeDesktopPaperSx },
          layout: { sx: { p: 4, borderRadius: 3 } },
          day: { sx: dateRangeDaySx },
          textField: {
            size: 'small',
            fullWidth: true,
            placeholder: 'Select date range',
            sx: dateRangeFieldSx,
            onClick: () => setOpen(true),
          },
          shortcuts: {
            changeImportance: 'set',
            items: shortcutItems,
            dense: false,
            sx: dateRangeShortcutsSx,
          },
          popper: { sx: { zIndex: 1400 } },
        }}
      />
    </FilterField>
  )
}

export default AnalyticsDateRangeDrawerField
